import { useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Box,
  Flex,
  Radio,
  RadioGroup,
  SimpleGrid,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Switch,
  useStyleConfig,
} from "@chakra-ui/react";
import { CARD_THEME_TYPES } from "./snapshotMaker.constants";

const SnapshotMakerAppearanceSettings = ({
  config = {},
  watermark,
  onConfigChange,
  onWatermarkChange,
}) => {
  const { t } = useTranslation(["snapshot-maker"]);
  const radioGroupStyles = useStyleConfig('RadioGroup', { variant: "switch" });
  
  const handleConfigChange = useCallback((name) => (value) => {
    onConfigChange?.(name, value);
  }, [onConfigChange]);

  const handleWatermarkChange = useCallback(() => {
    onWatermarkChange?.(!watermark);
  }, [onWatermarkChange, watermark]);

  return (
    <Box>
      <Box>
        <Box fontSize="xs" color="gray.100" textTransform="uppercase" marginBottom="2">
          {t("appearance.theme_color")}
        </Box>
        <RadioGroup
          onChange={handleConfigChange("themeColor")}
          value={config.themeColor}
          variant="switch"
          __css={radioGroupStyles}
        >
          {Object.values(CARD_THEME_TYPES).map((type) => (
            <Radio key={type} value={type}>
              {t(`theme_color.${type}`)}
            </Radio>
          ))}
        </RadioGroup>
      </Box>
      <SimpleGrid columns="2" spacing="2" spacingX="4" marginTop="2">
        <Box>
          <Box fontSize="xs" color="gray.100" textTransform="uppercase">
            {t("appearance.width")}
          </Box>
          <Slider
            variant="solid"
            value={config.width}
            min={1}
            max={2}
            step={0.01}
            onChange={handleConfigChange("width")}
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb />
          </Slider>
        </Box>
        <Box>
          <Box fontSize="xs" color="gray.100" textTransform="uppercase">
            {t("appearance.scale")}
          </Box>
          <Slider
            variant="solid"
            value={config.scale}
            min={1}
            max={1.5}
            step={0.01}
            onChange={handleConfigChange("scale")}
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb />
          </Slider>
        </Box>
        <Box>
          <Box fontSize="xs" color="gray.100" textTransform="uppercase">
            {t("appearance.corner_radius")}
          </Box>
          <Slider
            variant="solid"
            value={config.cornerRadius}
            min={0}
            max={2}
            step={0.1}
            onChange={handleConfigChange("cornerRadius")}
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb />
          </Slider>
        </Box>
        <Box>
          <Box fontSize="xs" color="gray.100" textTransform="uppercase">
            {t("appearance.shadow")}
          </Box>
          <Slider
            variant="solid"
            value={config.shadow}
            min={0}
            max={3}
            step={0.1}
            onChange={handleConfigChange("shadow")}
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb />
          </Slider>
        </Box>
      </SimpleGrid>
      <Box marginTop="4">
        <Flex alignItems="center">
          <Switch
            id="snapshot-maker-watermark"
            size="sm"
            isChecked={!!watermark}
            onChange={handleWatermarkChange}
          />
          <Box
            as="label"
            htmlFor="snapshot-maker-watermark"
            fontSize="sm"
            lineHeight="1"
            margin="0"
            marginLeft="2"
            cursor="pointer"
          >
            {t("appearance.watermark")}
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

SnapshotMakerAppearanceSettings.propTypes = {
  config: PropTypes.shape({
    themeColor: PropTypes.oneOf(Object.values(CARD_THEME_TYPES)),
    cornerRadius: PropTypes.number,
    shadow: PropTypes.number,
    width: PropTypes.number,
    scale: PropTypes.number,
  }),
  watermark: PropTypes.bool,
  onConfigChange: PropTypes.func,
  onWatermarkChange: PropTypes.func,
};

SnapshotMakerAppearanceSettings.defaultProps = {
  config: undefined,
  watermark: true,
  onConfigChange: undefined,
  onWatermarkChange: undefined,
};

export default SnapshotMakerAppearanceSettings;
